import { useState } from "react"
import { Kachel } from "@/components/terminal/Feld"
import { MEDIEN } from "@/components/terminal/felder"
import type { Vorauswahl } from "@/lib/terminalApi"

export interface MischungKopf {
  mischung: string
  chargen: string
  nc_typ: string
  gebinde: string
  medium: string
}

interface Props {
  vorauswahl: Vorauswahl
  start: Partial<MischungKopf>
  onWeiter: (kopf: MischungKopf) => void
  onZurueck: () => void
}

/** Neue Mischung: Nummer (Vorschlag), NC-Typ, Gebinde und Medium als große Kacheln. */
export function NeueMischung({ vorauswahl, start, onWeiter, onZurueck }: Props) {
  const [kopf, setKopf] = useState<MischungKopf>({
    mischung: start.mischung ?? vorauswahl.mischung_vorschlag ?? "",
    chargen: start.chargen ?? "",
    nc_typ: start.nc_typ ?? "",
    gebinde: start.gebinde ?? "",
    medium: start.medium ?? "Ethanol",
  })
  const [andererTyp, setAndererTyp] = useState(
    !!start.nc_typ && !vorauswahl.auswahl.nc_typ.includes(start.nc_typ),
  )
  const fertig = kopf.mischung.trim().length >= 4 && kopf.nc_typ && kopf.gebinde && kopf.medium
  const eingabe = "h-16 w-full rounded-xl border-2 border-hair/10 bg-surface px-4 font-mono text-2xl text-ink focus:border-accent focus:outline-none"
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="grid gap-1.5 text-sm text-ink2">
          Mischung
          <input className={eingabe} value={kopf.mischung} inputMode="numeric" autoComplete="off"
            onChange={(e) => setKopf({ ...kopf, mischung: e.target.value })} />
        </label>
        <label className="grid gap-1.5 text-sm text-ink2">
          Chargen (Zentrifugenvorlagen)
          <input className={eingabe} value={kopf.chargen} autoComplete="off" placeholder="z. B. 3252-56"
            onChange={(e) => setKopf({ ...kopf, chargen: e.target.value })} />
        </label>
      </div>
      <section className="grid gap-2">
        <h3 className="text-lg font-semibold">NC-Typ</h3>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          {vorauswahl.auswahl.nc_typ.map((t) => (
            <Kachel key={t} an={kopf.nc_typ === t && !andererTyp} onClick={() => { setAndererTyp(false); setKopf({ ...kopf, nc_typ: t }) }}>
              <span className="font-mono">{t}</span>
            </Kachel>
          ))}
          <Kachel an={andererTyp} onClick={() => { setAndererTyp(true); setKopf({ ...kopf, nc_typ: "" }) }} klein>
            anderer …
          </Kachel>
        </div>
        {andererTyp && (
          <input className={eingabe} autoFocus placeholder="z. B. ZH 19,5" value={kopf.nc_typ}
            onChange={(e) => setKopf({ ...kopf, nc_typ: e.target.value.toUpperCase() })} />
        )}
      </section>
      <section className="grid gap-2">
        <h3 className="text-lg font-semibold">Gebinde</h3>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          {vorauswahl.auswahl.gebinde.map((g) => (
            <Kachel key={g} an={kopf.gebinde === g} onClick={() => setKopf({ ...kopf, gebinde: g })}>{g}</Kachel>
          ))}
        </div>
      </section>
      <section className="grid gap-2">
        <h3 className="text-lg font-semibold">Anfeuchtmittel</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {MEDIEN.map((m) => (
            <Kachel key={m.id} an={kopf.medium === m.id} onClick={() => setKopf({ ...kopf, medium: m.id })}>
              {m.id} <span className="font-normal text-ink2">({m.kurz})</span>
            </Kachel>
          ))}
        </div>
      </section>
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={!fertig} onClick={() => onWeiter({ ...kopf, mischung: kopf.mischung.trim() })}
          className="h-16 flex-1 rounded-xl bg-accent-strong px-6 text-xl font-semibold text-white disabled:opacity-40">
          Weiter zu den Werten
        </button>
        <button type="button" onClick={onZurueck} className="h-16 rounded-xl border-2 border-hair/10 bg-surface px-6 text-lg">
          Zurück
        </button>
      </div>
    </div>
  )
}
